/**
 * useReconcileStocks — performance fixes
 *
 * Key changes vs original:
 *  1. productsList is derived with useMemo (not recomputed on every render).
 *  2. The auto-save interval uses a ref for reconciliedData so it never
 *     recreates the interval.
 *  3. saveDraft / submitReconciliation both use refs to read latest
 *     reconciliedData without listing it as a dep (avoids stale-closure
 *     recreations that previously forced child re-renders).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getReconciliationsProducts,
  submitReconciledData,
  saveReconciledDraft,
  getReconciliationDiscrepancyReasons,
} from "../../../../../services/inventory";
import {
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { RequestLoader, ToastContext } from "../../../../../hooks/context";
import { cleanObject, getRandomIntInclusive } from "../../../../../helpers";
import {
  useGetProductCategoryList,
  useGetReconciliationDetails,
} from "../../../../../hooks/api/queries/useInventory";
import { useGetInfo } from "../../../../../hooks/useGetInfo";

const useReconcileStocks = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [reconciliedData, setReconciliedData] = useState({});
  const [stockCountDate, setStockCountDate] = useState("");
  const [isReconciliationPrompt, setIsReconciliationPrompt] = useState(false);
  const navigate = useNavigate();
  const triggerToast = useContext(ToastContext);
  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const queryClient = useQueryClient();
  const store_id = searchParams.get("store_id");
  const { getStoreInfo } = useGetInfo();
  const storeDetails = getStoreInfo(store_id);

  const [filters, setFilters] = useState({
    categories: [],
    searchQuery: "",
    id: id,
    isReview: false,
    stock_status: "",
  });

  const stockStatusList = useMemo(
    () => [
      { name: "All", value: "" },
      { name: "In Stock", value: "IN_STOCK" },
      { name: "Out of Stock", value: "OUT_OF_STOCK" },
    ],
    [],
  );

  const { data: categoryList } = useGetProductCategoryList();

  const {
    isLoading: isFetchingReconciliationDetails,
    data: reconciliedDetails,
  } = useGetReconciliationDetails({
    id,
    isEnabled: id ? true : false,
  });

  const hasInitializedDate = useRef(false);

  useEffect(() => {
    if (!hasInitializedDate.current && reconciliedDetails?.stock_count_date) {
      setStockCountDate(reconciliedDetails.stock_count_date);
      hasInitializedDate.current = true;
    }
  }, [reconciliedDetails?.stock_count_date]);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const { isLoading, data, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery(
      ["getReconciliationsProducts", filters],
      ({ pageParam }) =>
        getReconciliationsProducts(
          cleanObject({
            id: filters?.id,
            page: pageParam,
            query: filters?.searchQuery,
            category_ids: filters?.categories?.join("|"),
            isReview: filters?.isReview,
            exclude_on_demand_composite_products: true,
            stock_status: filters?.stock_status,
          }),
        ).then((res) => res?.data),
      {
        staleTime: Infinity,
        getNextPageParam: ({ meta: { next_page } }) =>
          next_page === null ? undefined : next_page,
      },
    );

  const { data: discrepancyReasons, isLoading: isLoadingDiscrepancy } =
    useQuery(
      ["getReconciliationDiscrepancyReasons"],
      () =>
        getReconciliationDiscrepancyReasons().then((res) => res?.data?.data),
      { staleTime: Infinity },
    );

  // ── productsList: memoised flat array ─────────────────────────────────────
  // Original code did `[].concat(...products.values())` inline on every render.
  // useMemo ensures we only recompute when data pages actually change.
  const productsList = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page?.data ?? []);
  }, [data?.pages]);

  // ── stable ref for reconciliedData so callbacks don't recreate ────────────
  const reconciliedDataRef = useRef(reconciliedData);
  useEffect(() => {
    reconciliedDataRef.current = reconciliedData;
  }, [reconciliedData]);

  // ── mutations ─────────────────────────────────────────────────────────────
  const { mutate: submitReconciled, isLoading: isSubmitting } = useMutation(
    (data) => submitReconciledData(data),
  );

  const submitReconciliation = useCallback(() => {
    const submitMap = new Map();

    // 1. Products with an existing physical count (e.g. after refresh or from draft)
    productsList.forEach((p) => {
      const phys =
        p?.current_physical_count !== undefined &&
        p?.current_physical_count !== null
          ? p.current_physical_count
          : p?.physical_count;
      if (phys !== null && phys !== undefined && phys !== "") {
        const sys =
          p?.current_system_count !== undefined &&
          p?.current_system_count !== null
            ? p.current_system_count
            : p?.system_count;
        submitMap.set(p.item_id, {
          ...p,
          physical_count: phys,
          current_physical_count: phys,
          system_count: sys,
        });
      }
    });

    // 2. Any item currently in reconciliedDataRef takes precedence (local edits)
    Object.values(reconciliedDataRef.current || {}).forEach((item) => {
      if (
        item?.physical_count !== null &&
        item?.physical_count !== undefined &&
        item?.physical_count !== ""
      ) {
        const sys =
          item.current_system_count !== undefined &&
          item.current_system_count !== null
            ? item.current_system_count
            : item.system_count;
        submitMap.set(item.item_id, {
          ...item,
          physical_count: item.physical_count,
          current_physical_count: item.physical_count,
          system_count: sys,
        });
      }
    });

    const payload = (
      submitMap.size > 0
        ? Array.from(submitMap.values())
        : Object.values(reconciliedDataRef.current)
    ).map((item) => {
      const phys =
        item.current_physical_count !== undefined &&
        item.current_physical_count !== null
          ? item.current_physical_count
          : item.physical_count;
      const sys =
        item.current_system_count !== undefined &&
        item.current_system_count !== null
          ? item.current_system_count
          : item.system_count;
      return {
        ...item,
        system_count: sys,
        physical_count: phys,
      };
    });

    submitReconciled(
      {
        data: cleanObject({
          data: payload,
          stock_count_date: stockCountDate,
        }),
        id,
      },
      {
        onSuccess: () => {
          setReconciliedData({});
          triggerToast(
            "Reconciliation has been submitted for review",
            "success",
          );
          queryClient.invalidateQueries(["getReconciliationsProducts"]);
          queryClient.invalidateQueries(["getReconciliationDetails"]);
          queryClient.invalidateQueries(["getReconciliations"]);
          navigate(-1);
        },
      },
    );
  }, [
    productsList,
    submitReconciled,
    id,
    stockCountDate,
    triggerToast,
    queryClient,
    navigate,
  ]);

  const isMissingDiscrepancyReason = (item) => {
    const effectiveSystem =
      item.system_count !== null && item.system_count !== undefined
        ? item.system_count
        : (item.current_system_count ?? item.system_count);
    return (
      item.physical_count !== null &&
      item.physical_count !== "" &&
      Number(item.physical_count) !== Number(effectiveSystem) &&
      !item.discrepancy_reason_id
    );
  };

  const handleSubmitClick = useCallback(() => {
    const missingReason = Object.values(reconciliedDataRef.current).some(
      isMissingDiscrepancyReason,
    );

    if (missingReason) {
      triggerToast(
        "Please select a discrepancy reason for all items with a count difference",
        "warning",
      );
      return;
    }

    setIsReconciliationPrompt(true);
  }, [triggerToast]);

  const { mutate: saveReconciled, isLoading: isSaving } = useMutation((data) =>
    saveReconciledDraft(data),
  );

  const saveDraft = useCallback(
    (auto = true) => {
      const entries = Object.entries(reconciliedDataRef.current);
      const validEntries = entries.filter(
        ([, item]) => !isMissingDiscrepancyReason(item),
      );

      if (validEntries.length === 0) {
        if (!auto && entries.length > 0) {
          triggerToast(
            "Please select a discrepancy reason before saving",
            "warning",
          );
        }
        return;
      }

      setRequestLoaderProgress(getRandomIntInclusive(20, 50));
      const payload = validEntries.map(([, item]) => ({
        ...item,
        system_count:
          item.current_system_count !== undefined &&
          item.current_system_count !== null
            ? item.current_system_count
            : item.system_count,
      }));
      const savedKeys = validEntries.map(([key]) => key);

      saveReconciled(
        {
          data: cleanObject({
            data: payload,
            stock_count_date: stockCountDate,
          }),
          id,
        },
        {
          onSuccess: () => {
            setReconciliedData((prev) => {
              const next = { ...prev };
              savedKeys.forEach((key) => delete next[key]);
              return next;
            });
            if (!auto) {
              triggerToast("Saved!", "success");
              queryClient.invalidateQueries(["getReconciliationsProducts"]);
              queryClient.invalidateQueries(["getReconciliationDetails"]);
              queryClient.invalidateQueries(["getReconciliations"]);
            }
          },
          onSettled: () => setRequestLoaderProgress(100),
        },
      );
    },
    [
      saveReconciled,
      id,
      stockCountDate,
      setRequestLoaderProgress,
      triggerToast,
      queryClient,
    ],
  );

  const refreshReconciliation = useCallback(() => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setRequestLoaderProgress(getRandomIntInclusive(20, 50));

    // Gather items with physical count to save draft with new system count
    const draftMap = new Map();

    // 1. Products with an existing physical count
    productsList.forEach((p) => {
      const phys =
        p?.current_physical_count !== undefined &&
        p?.current_physical_count !== null
          ? p.current_physical_count
          : p?.physical_count;
      if (phys !== null && phys !== undefined && phys !== "") {
        const sys =
          p?.current_system_count !== undefined &&
          p?.current_system_count !== null
            ? p.current_system_count
            : p?.system_count;
        draftMap.set(p.item_id, {
          ...p,
          physical_count: phys,
          system_count: sys,
        });
      }
    });

    // 2. Any item currently in reconciliedDataRef takes precedence (local edits)
    Object.values(reconciliedDataRef.current || {}).forEach((item) => {
      if (
        item?.physical_count !== null &&
        item?.physical_count !== undefined &&
        item?.physical_count !== ""
      ) {
        const sys =
          item.current_system_count !== undefined &&
          item.current_system_count !== null
            ? item.current_system_count
            : item.system_count;
        draftMap.set(item.item_id, {
          ...item,
          system_count: sys,
        });
      }
    });

    if (draftMap.size > 0) {
      const payload = Array.from(draftMap.values()).map((item) => ({
        ...item,
        system_count:
          item.current_system_count !== undefined &&
          item.current_system_count !== null
            ? item.current_system_count
            : item.system_count,
      }));

      const savedKeys = Array.from(draftMap.keys());

      saveReconciled(
        {
          data: cleanObject({
            data: payload,
            stock_count_date: stockCountDate,
          }),
          id,
        },
        {
          onSuccess: () => {
            setReconciliedData((prev) => {
              const next = { ...prev };
              savedKeys.forEach((key) => delete next[key]);
              return next;
            });
            queryClient.invalidateQueries(["getReconciliationsProducts"]);
            queryClient.invalidateQueries(["getReconciliationDetails"]);
            queryClient.invalidateQueries(["getReconciliations"]);
            triggerToast("Refreshed and draft updated", "success");
          },
          onSettled: () => {
            setRequestLoaderProgress(100);
            setIsRefreshing(false);
          },
        },
      );
    } else {
      queryClient.invalidateQueries(["getReconciliationsProducts"]);
      queryClient.invalidateQueries(["getReconciliationDetails"]);
      queryClient.invalidateQueries(["getReconciliations"]);
      triggerToast("Product list refreshed", "success");
      setRequestLoaderProgress(100);
      setIsRefreshing(false);
    }
  }, [
    isRefreshing,
    productsList,
    saveReconciled,
    stockCountDate,
    id,
    setRequestLoaderProgress,
    queryClient,
    triggerToast,
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (Object.keys(reconciliedDataRef.current).length > 0) saveDraft(true);
    }, 50_000);
    return () => clearInterval(interval);
  }, [saveDraft]);

  const isDraft = reconciliedDetails?.status
    ? reconciliedDetails?.status === "DRAFT"
    : true;

  return {
    storeName: storeDetails?.name,
    storeDetails,
    discrepancyReasons: discrepancyReasons || [],
    isLoadingDiscrepancy,
    isSaving,
    isSubmitting,
    products: productsList,
    isLoading: isLoading || isFetchingReconciliationDetails,
    data,
    isFetchingNextPage,
    hasNextPage,
    reconciliedData,
    isReconciliationPrompt,
    categoryList: categoryList || [],
    filters,
    searchParams,
    stockStatusList,
    setFilters,
    submitReconciliation,
    setReconciliedData,
    saveDraft,
    refreshReconciliation,
    isRefreshing,
    handleSubmitClick,
    setIsReconciliationPrompt,
    stockCountDate,
    setStockCountDate,
    fetchNextPage,
    status: reconciliedDetails?.status,
    isDraft,
  };
};

export default useReconcileStocks;
